/**
 * Auto Add Free Gift (multi-promo)
 * --------------------------------------------------------------------------
 * Keeps one or more hidden "free gift" variants in the cart whose quantity
 * tracks the total quantity of their matching "trigger" products. Each promo
 * is independent: trigger set A keeps gift A in sync, trigger set B keeps
 * gift B in sync, etc.
 *
 * Source of truth for promos:
 *
 *   window.AutoAddFreeGiftConfig.promos
 *     Rendered server-side by layout/theme.liquid from the
 *     `free_gift_promo` metaobject definition in Shopify admin. If no
 *     valid entries are present, the script does nothing.
 *
 * To add, remove, or change a promo: edit the metaobject entries in
 * Shopify admin (Settings > Custom data > Metaobjects > Free Gift Promo).
 * No code change is ever needed.
 *
 * Free gift lines are tagged with two private line item properties:
 *   - _auto_added       : 'true'         (used by snippets/cart-form.liquid
 *                                         and the badge/lock CSS)
 *   - _auto_added_promo : '<promo.name>' (lets the reconciler tell each
 *                                         promo's gift line apart)
 *
 * Legacy lines that only carry `_auto_added: 'true'` (from earlier
 * single-promo versions of this script) are still recognized and bound
 * to whichever current promo owns their variant id, so in-flight customer
 * carts keep working across the upgrade.
 */
(function () {
  'use strict';

  /** Set true while developing to see what the reconciler is doing. */
  var DEBUG = false;

  /* ====================================================================== *
   * Implementation. You normally do not need to edit below this line.
   * ====================================================================== */

  var FLAG_PROPERTY = '_auto_added';
  var FLAG_PROMO_PROPERTY = '_auto_added_promo';
  var ROOT =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
    '/';
  var CART_MUTATING = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/i;

  var reconciling = false;
  var rerun = false;

  function log() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[auto-add]');
    console.log.apply(console, args);
  }

  function warn() {
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[auto-add]');
    console.warn.apply(console, args);
  }

  function err() {
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[auto-add]');
    console.error.apply(console, args);
  }

  function promoValid(promo) {
    return (
      promo &&
      typeof promo.name === 'string' &&
      promo.name.length > 0 &&
      Array.isArray(promo.triggerIds) &&
      promo.triggerIds.length > 0 &&
      typeof promo.freeVariantId === 'number' &&
      promo.freeVariantId > 0
    );
  }

  function getPromos() {
    if (
      !window.AutoAddFreeGiftConfig ||
      !Array.isArray(window.AutoAddFreeGiftConfig.promos)
    ) {
      return [];
    }
    return window.AutoAddFreeGiftConfig.promos.filter(promoValid);
  }

  function isTrigger(item, promo) {
    return (
      promo.triggerIds.indexOf(item.variant_id) !== -1 ||
      promo.triggerIds.indexOf(item.product_id) !== -1
    );
  }

  function promoActive(promo) {
    if (!promo.endDate) return true;
    var end = new Date(promo.endDate).getTime();
    if (isNaN(end)) return true;
    return Date.now() <= end;
  }

  function triggerQuantity(cart, promo) {
    var total = 0;
    for (var i = 0; i < cart.items.length; i++) {
      if (isTrigger(cart.items[i], promo)) total += cart.items[i].quantity;
    }
    var cap = promo.maxQty || 0;
    if (cap > 0 && total > cap) total = cap;
    return total;
  }

  function findFreeLine(cart, promo) {
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (!item.properties) continue;
      if (String(item.properties[FLAG_PROMO_PROPERTY]) === promo.name) {
        return item;
      }
      if (
        String(item.properties[FLAG_PROPERTY]) === 'true' &&
        !item.properties[FLAG_PROMO_PROPERTY] &&
        item.variant_id === promo.freeVariantId
      ) {
        return item;
      }
    }
    return null;
  }

  function getCart() {
    return fetch(ROOT + 'cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then(function (r) {
      return r.json();
    });
  }

  function readResponse(label, response) {
    return response.text().then(function (text) {
      var body;
      try {
        body = JSON.parse(text);
      } catch (e) {
        body = text;
      }
      if (!response.ok) {
        err(label + ' returned ' + response.status, body);
        var error = new Error(
          label + ' failed: ' + response.status + ' ' + response.statusText
        );
        error.status = response.status;
        error.body = body;
        throw error;
      }
      log(label + ' ok', body);
      return body;
    });
  }

  function addFreeGift(promo, qty) {
    var props = {};
    props[FLAG_PROPERTY] = 'true';
    props[FLAG_PROMO_PROPERTY] = promo.name;
    var body = {
      items: [
        {
          id: promo.freeVariantId,
          quantity: qty,
          properties: props,
        },
      ],
    };
    log('add', promo.name, body);
    return fetch(ROOT + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    }).then(function (r) {
      return readResponse('cart/add.js', r);
    });
  }

  function changeLine(key, qty) {
    log('change', key, qty);
    return fetch(ROOT + 'cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({ id: key, quantity: qty }),
    }).then(function (r) {
      return readResponse('cart/change.js', r);
    });
  }

  function reconcile() {
    var promos = getPromos();
    if (promos.length === 0) {
      warn(
        'no valid promos found in window.AutoAddFreeGiftConfig. ' +
          'Create a Free Gift Promo metaobject entry in Shopify admin ' +
          '(Settings > Custom data > Metaobjects) and make sure the ' +
          'name, trigger_products, and free_gift_variant fields are filled.'
      );
      return Promise.resolve();
    }
    if (reconciling) {
      rerun = true;
      return Promise.resolve();
    }
    reconciling = true;

    return getCart()
      .then(function (cart) {
        var anyMutation = false;
        return promos
          .reduce(function (chain, promo) {
            return chain.then(function () {
              var active = promoActive(promo);
              var desired = active ? triggerQuantity(cart, promo) : 0;
              var free = findFreeLine(cart, promo);
              log('reconcile', promo.name, {
                active: active,
                desired: desired,
                free: free && free.quantity,
              });

              var action = null;
              if (desired > 0 && !free) {
                action = addFreeGift(promo, desired);
              } else if (desired > 0 && free && free.quantity !== desired) {
                action = changeLine(free.key, desired);
              } else if (desired === 0 && free) {
                action = changeLine(free.key, 0);
              }

              if (action) {
                anyMutation = true;
                return action;
              }
              return null;
            });
          }, Promise.resolve())
          .then(function () {
            return anyMutation;
          });
      })
      .then(function (mutated) {
        if (mutated) {
          document.dispatchEvent(
            new CustomEvent('cart:auto-add-updated', { bubbles: true })
          );
        }
      })
      .catch(function (e) {
        err('reconcile failed', e);
      })
      .then(function () {
        reconciling = false;
        if (rerun) {
          rerun = false;
          reconcile();
        }
      });
  }

  function scheduleReconcile() {
    if (reconciling) {
      rerun = true;
      return;
    }
    Promise.resolve().then(reconcile);
  }

  /* ---- intercept all cart mutations made by the theme or other apps ----- */

  var originalFetch = window.fetch;
  window.fetch = function () {
    var args = arguments;
    var input = args[0];
    var url = typeof input === 'string' ? input : input && input.url;
    var watch = url && CART_MUTATING.test(url);
    var promise = originalFetch.apply(this, args);
    if (watch) {
      promise
        .then(function () {
          if (!reconciling) scheduleReconcile();
        })
        .catch(function () {});
    }
    return promise;
  };

  var OriginalXHR = window.XMLHttpRequest;
  function PatchedXHR() {
    var xhr = new OriginalXHR();
    var open = xhr.open;
    var watchedUrl = '';
    xhr.open = function (method, url) {
      watchedUrl = url || '';
      return open.apply(xhr, arguments);
    };
    xhr.addEventListener('loadend', function () {
      if (CART_MUTATING.test(watchedUrl) && !reconciling) scheduleReconcile();
    });
    return xhr;
  }
  PatchedXHR.prototype = OriginalXHR.prototype;
  window.XMLHttpRequest = PatchedXHR;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile);
  } else {
    reconcile();
  }

  /* ---- expose a tiny debug helper on window ----------------------------- *
   * From the browser console:
   *   AutoAddFreeGift.promos              the active promo list (from the metaobject)
   *   AutoAddFreeGift.cart()              current cart from /cart.js
   *   AutoAddFreeGift.reconcile()         force a reconcile pass
   *   AutoAddFreeGift.checkFreeVariants() probe /variants/<id>.js for each promo
   *                                       to confirm the free variants exist & are
   *                                       published to Online Store
   * --------------------------------------------------------------------- */
  window.AutoAddFreeGift = {
    get promos() {
      return getPromos();
    },
    cart: getCart,
    reconcile: reconcile,
    checkFreeVariants: function () {
      var promos = getPromos();
      return Promise.all(
        promos.map(function (promo) {
          return fetch(ROOT + 'variants/' + promo.freeVariantId + '.js', {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
          }).then(function (r) {
            if (!r.ok) {
              err(
                'free variant lookup failed for promo "' +
                  promo.name +
                  '" (' +
                  r.status +
                  '). The ID ' +
                  promo.freeVariantId +
                  ' is probably a PRODUCT id, not a VARIANT id, or the product is not published to the Online Store channel.'
              );
              return { promo: promo.name, ok: false, status: r.status };
            }
            return r.json().then(function (v) {
              log('free variant ok', promo.name, v);
              return { promo: promo.name, ok: true, variant: v };
            });
          });
        })
      );
    },
    setDebug: function (v) {
      DEBUG = !!v;
      log('debug set to', DEBUG);
    },
  };
})();
